// https://github.com/DrupalizeMe/react-and-drupal-examples/tree/master/react-decoupled
// Fuzzy search example: https://fusejs.io/demo.html
// https://www.google.com/search?q=nextjs+redirect+client+side+to+a+page&rlz=1C5CHFA_enUS805US805&ei=3iSbYeDlNuqYwbkPp52KqA8&ved=0ahUKEwjgzOj6mKv0AhVqTDABHaeOAvUQ4dUDCA4&uact=5&oq=nextjs+redirect+client+side+to+a+page&gs_lcp=Cgdnd3Mtd2l6EAMyBQgAEM0CMgUIABDNAjIFCAAQzQI6BwgAEEcQsAM6BggAEA0QCjoECCEQCkoECEEYAFCYBljrFGCZFmgBcAJ4AIABUogBswaSAQIxMpgBAKABAcgBCMABAQ&sclient=gws-wiz#kpvalbx=_-iSbYab_CLiMwbkP1Mus-A428
import React, {useContext, useEffect, useState, useRef} from 'react';
import _ from 'lodash';
import LinearProgress from '@mui/material/LinearProgress';
import dynamic from 'next/dynamic';
import {useForm, Controller} from 'react-hook-form';
import makeStyles from '@mui/styles/makeStyles';
import Button from '@mui/material/Button';
import Grid from '@mui/material/Grid';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import TextField from "@mui/material/TextField";
import MenuItem from '@mui/material/MenuItem';

const ListingsGrid = dynamic(() => import('@/components/02_molecules/ContentList'), {ssr: false});
import parse from 'html-react-parser';
import Accordion from '@mui/material/Accordion';
import AccordionSummary from '@mui/material/AccordionSummary';
import AccordionDetails from '@mui/material/AccordionDetails';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import {isMobile} from "react-device-detect";
import CurrentPageContext from "@/components/context/CurrentPageData";
import {getSolrClient} from "@/utils/solr";
import {useRouter} from 'next/router';

const useStyles = makeStyles((theme) => ({
  root: {
    borderBottom: '1px solid ' + theme.palette.primary.main,
    marginBottom: '2rem',

    '& .MuiInput-underline:before': {
      border: 0,
    },
    '& .MuiNativeSelect-icon': {
      display: 'none',
    },
    '& ul[role="listbox"]': {
      margin: '0 !important',
    },
    '& .reset-btn': {
      fontSize: '.8rem',
    }
  },
}));

const ConditionalWrapper = ({condition, wrapper, children}) =>
    condition ? wrapper(children) : children;
const SearchNoResults = () => {
  return (
      <Typography component={`p`}>Current search didn't return any results.
        Adjust the filters and search again.
      </Typography>
  );
}

// This is for resetting back to intial state, non-modifiable:
const form_default_reset = {
  txt: '',
  community: 0,
  price: 0,
  beds: 0,
  baths: 0,
  sort: [{dir: 'DESC', field: 'its_brokerage'}],
  page: 0,
}

// These are initial settings, but get altered down the road:
let form_default = {
  txt: '',
  community: 0,
  price: 0,
  beds: 0,
  baths: 0,
  sort: [{dir: 'DESC', field: 'its_brokerage'}],
  page: 0,
}

const SalesforceSearchForm = (props) => {
  let {block, node, key} = props;
  node = (!node && block) ? block : node;
  if (!node) {
    return null;
  }

  let {filtersYn, body, ctaLinks} = node;
  let new_block = null;
  const sitewideContext = useContext(CurrentPageContext);

  // The main query: Object with params for "/api/rest/view/rets-listings" view contextual filters.
  let lq = {
    domSrc: sitewideContext?.site || 'all',
  };

  if (node) {
    if (node.site?.length > 0) {
      lq['propType'] = node.site;
    }

    if (node.homeLot?.length > 0) {
      let vals = [];
      node.homeLot.map((h, i) => {
        vals.push(h); // vals.push(encodeURI(h));
      });
      lq['homeLot'] = vals.join('%2B'); // %2B is encoded +
    }

    if (node.saleType?.length > 0) {
      let vals = [];
      node.saleType.map((h, i) => {
        vals.push(h);
      });
      lq['saleType'] = vals.join('+'); // %2B is encoded +
    }

    if (node.homeStatus?.length > 0) {
      let vals = [];
      node.homeStatus.map((h, i) => {
        vals.push(h);
      });
      lq['homeStatus'] = vals.join('+'); // %2B is encoded +
    }
  }

  let view_modes = {
    'card': 'TunnellListingCard',
    'teaser': 'TunnellListingTeaser'
  };

  const currFormRef = useRef(null);
  const router = useRouter()
  const solrClient = getSolrClient();
  const classes = useStyles();

  // Query string for browser history:
  const [urlQueryObj, setUrlQueryObj] = useState({
    forceFormUpdate: false,
    q: (typeof window !== "undefined") ? decodeURI(window.location.search)?.slice(1) : null,
  });

  const [resetPager, setResetPager] = useState(null);

  if (urlQueryObj.q?.length) {
    let newSettOverride = null;
    let q = {};
    const url = new URL(router.asPath, process.env.NEXT_PUBLIC_FRONT_URL);
    const urlParams = new URLSearchParams(url.search);

    for (const entry of urlParams?.entries()) {
      q[entry[0]] = entry[1];
    }

    // Parse search params from URL query:
    newSettOverride = solrClient.parseUrlViewQuery(q);

    form_default = {...form_default, ...newSettOverride};
    lq = {...lq, ...newSettOverride};
  }

  new_block = {
    key: node?.id?.substring(node.id.length - 7) || 'search-block',
    pageCurr: lq['pager'] || 0,
    perPage: parseInt(node?.pager?.first) || 4,
    noPager: node?.pagerYn || false,
    colMd: (node?.pager?.second && (12 / parseInt(node?.pager?.second))) || 4,
    spacing: 2,
    fetchOpts: {
      url: `/api/rest/view/rets-listings`,
      query: lq,
      opts: {type: 'tld', dbview: 'json'},
    },
    comp: node?.viewMode ? view_modes[node.viewMode] : `TunnellListingCard`,
  };

  let {
    handleSubmit,
    control,
    watch,
    getValues,
    resetField,
    reset,
    formState: {errors, isSubmitting, isSubmitted}
  } = useForm({
    defaultValues: form_default
  });

  const [searchQ, setSearchQ] = useState(new_block);

  /* RESET form handler */
  const handleSearchFormReset = (event) => {
    reset(form_default_reset);
    router.push(window.location.origin + window.location.pathname, undefined, {shallow: true});
  };

  /* SUBMIT form handler */
  const onSubmit = async (form_data) => {
    let new_lq = {...lq};
    new_block['fetchOpts']['query'] = null;


    if (form_data.beds == 0 && lq['beds']) {
      delete new_lq['beds'];
      // resetField('beds');
    }
    else if (form_data.beds > 0) {
      new_lq['beds'] = form_data.beds;
    }

    if (form_data.baths == 0 && lq['bathAll']) {
      delete new_lq['bathAll'];
    }
    else if (form_data.baths > 0) {
      if (new_lq['baths']) {
        delete new_lq['baths'];
      }
      new_lq['bathAll'] = form_data.baths;
    }

    if (form_data.community == 0) {
      delete new_lq['commTerm'];
      delete new_lq['community'];
    }
    else if (form_data.community > 0) {
      new_lq['commTerm'] = form_data.community;
      new_lq['community'] = form_data.community;
    }

    if (form_data.price == 0 && lq['price']) {
      delete new_lq['price'];
      delete new_lq['price[min]'];
      delete new_lq['price[max]'];
    }
    else if (form_data.price?.length > 1) {
      if (new_lq['price']) {
        delete new_lq['price'];
      }
      let prices = form_data.price.split('_');
      if (prices[1] === '0') {
        new_lq['price[min]'] = prices[0];
      }
      else {
        new_lq['price[min]'] = prices[0];
        new_lq['price[max]'] = prices[1];
      }
    }

    if (form_data.txt == '' && lq['txt']) {
      delete new_lq['txt'];
      // resetField('txt');
    }
    else if (form_data.txt?.length > 1) {
      new_lq['txt'] = form_data.txt;
    }

    new_block['fetchOpts']['query'] = new_lq;

    let share_query = solrClient.assembleJsonApiSearchShareQuery(form_data, router);
    // Add search query to Browser History:
    if (share_query || share_query?.length > 0) {
      router.push(share_query, undefined, {shallow: true});
    }
    else {
      router.push(window.location.pathname, undefined, {shallow: true});
    }

    // Assigning the array/object is copying the reference - and React wouldn't
    // see that as a change - since the ref to the array/object isn't being
    // changed - only content within it. So, create new var:
    let newItems = null;
    newItems = {...new_block};
    const res_del = delete (newItems['fetchOpts']['query']);

    newItems['fetchOpts']['query'] = new_lq;

    // Reset the Pager state so that Search query doesn't include paginated request:
    setResetPager(-1);
    setSearchQ(newItems);
    // Reset the Pager state so that Pager component is set back to a page state = 0:
    setResetPager(null);
  };

  let debouncedSearch = _.debounce(handleSubmit(onSubmit), 1200);
  useEffect(() => {
    const subscription = watch((data, someObj) => {
      // const {name, type} = someObj;
      debouncedSearch(data, 1200);
    });
    return () => subscription.unsubscribe();
  }, [watch]);

  return (
    <Box key={`sf-search-comp-${key}`}>
      {searchQ &&
        <>
          {body?.length > 0 &&
            <Typography component={`div`}
              dangerouslySetInnerHTML={{ __html: body }}></Typography>
          }

          {filtersYn &&
            <div className={classes.root}>
              {isSubmitted && errors && Object.keys(errors).length > 0 &&
                <div>
                  <Typography color="error"><strong>Errors detected</strong><br /></Typography>
                </div>
              }
              {isSubmitting &&
                <div><LinearProgress /></div>
              }
              <Box displayPrint="none" my={isMobile ? 4 : undefined}>

                <ConditionalWrapper condition={isMobile}
                  wrapper={children => <Accordion style={{ boxShadow: "none", padding: 0 }}>
                    <AccordionSummary style={{ padding: 0 }}
                      expandIcon={<ExpandMoreIcon />}
                      aria-controls="panel1a-content"
                      id="panel1a-header"
                    >
                      <Typography className={classes.heading}>Filter Results</Typography>
                    </AccordionSummary>
                    <AccordionDetails style={{ padding: 0 }}>

                      {children}

                    </AccordionDetails>
                  </Accordion>
                  }>

                  <form noValidate className="search-form" autoComplete="off" key={`listing-search-form`}
                    onSubmit={handleSubmit(onSubmit)} ref={currFormRef}>
                    <Grid container spacing={isMobile ? 4 : 2}>
                      <Grid item xs={12} md={isMobile ? 3 : 2}>
                        <Box mb={isMobile ? 1 : undefined}>
                          <Controller name="txt" control={control}
                            render={({ field }) => (
                              <TextField {...field} id="edit-txt"
                                label="Search listings"
                                variant="outlined" key={`edit-txt`}
                                inputProps={{ maxLength: 100 }}
                                type="text"
                                placeholder="Keyword search"
                                fullWidth
                              />
                            )}
                          />
                        </Box>
                      </Grid>

                  {sitewideContext?.site !== 'baywoods' &&
                  <Grid item xs={12} md={isMobile ? 3 : 2}>
                    <Box mb={isMobile ? 1 : undefined}>
                      <Controller name="community" control={control}
                                  render={({field}) => (
                                    <TextField {...field}
                                               id="edit-community"
                                               select label="Community"
                                               fullWidth variant="outlined"
                                               key={`edit-community`}
                                    >
                                      <MenuItem key={`comm-all`}
                                                value={0}><em>-Any-</em></MenuItem>
                                      <MenuItem key={`comm-3`}
                                                value={3}>Bayside</MenuItem>
                                      <MenuItem key={`comm-5`}
                                                value={5}>Creekside</MenuItem>
                                      <MenuItem key={`comm-6`}
                                                value={6}>Coveside</MenuItem>
                                      <MenuItem key={`comm-7`}
                                                value={7}>Dockside</MenuItem>
                                      <MenuItem key={`comm-1`}
                                                value={1}>Lakeside</MenuItem>
                                      <MenuItem key={`comm-8`} value={8}>Lakeside
                                        Bay Farm Road</MenuItem>
                                      <MenuItem key={`comm-4`}
                                                value={4}>Seaside</MenuItem>
                                      {/*<MenuItem key={`comm-11`} value={11}>Floor Plans</MenuItem>*/}
                                    </TextField>
                                  )}
                      />
                    </Box>
                  </Grid>
                  }
                  <Grid item xs={12} md={2}>
                    <Box mb={isMobile ? 1 : undefined}>
                      <Controller name="price" control={control}
                                  render={({field}) => (
                                    <TextField {...field} id="edit-price" select
                                               label="Price"
                                               fullWidth variant="outlined"
                                               key={`edit-price`}
                                    >
                                      <MenuItem key={`price-all`}
                                                value={0}><em>-Any-</em></MenuItem>
                                      <MenuItem key={`price-1`}
                                                value={`0_50000`}>Up
                                        to $ 50,000</MenuItem>
                                      <MenuItem key={`price-2`}
                                                value={`50000_100000`}>$ 50,000
                                        -
                                        $ 100,000</MenuItem>
                                      <MenuItem key={`price-3`}
                                                value={`100000_150000`}>$
                                        100,000
                                        - $ 150,000</MenuItem>
                                      <MenuItem key={`price-4`}
                                                value={`150000_200000`}>$
                                        150,000
                                        - $ 200,000</MenuItem>
                                      <MenuItem key={`price-5`}
                                                value={`200000_250000`}>$
                                        200,000
                                        - $ 250,000</MenuItem>
                                      <MenuItem key={`price-6`}
                                                value={`250000_300000`}>$
                                        250,000
                                        - $ 300,000</MenuItem>
                                      <MenuItem key={`price-7`}
                                                value={`300000_350000`}>$
                                        300,000
                                        - $ 350,000</MenuItem>
                                      <MenuItem key={`price-8`}
                                                value={`350000_0`}>$
                                        350,000+</MenuItem>
                                    </TextField>
                                  )
                                  }
                      />
                    </Box>
                  </Grid>

                  <Grid item xs={12} md={2}>
                    <Box mb={isMobile ? 1 : undefined}>
                      <Controller name="beds" control={control}
                                  render={({field}) => (
                                    <TextField {...field} id="edit-beds" select
                                               fullWidth label="Beds"
                                               variant="outlined"
                                               key={`edit-beds`}
                                    >
                                      <MenuItem key={`beds-all`}
                                                value={0}><em>-Any-</em></MenuItem>
                                      {
                                        [1, 2, 3, 4, 5, 6, 7, 8, 9, 10].map((v, i) => {
                                          return <MenuItem key={`beds-${i}`}
                                                           value={v}>{v}</MenuItem>
                                        })
                                      }
                                    </TextField>
                                  )
                                  }
                      />
                    </Box>
                  </Grid>

                  <Grid item xs={12} md={2}>
                    <Box mb={isMobile ? 1 : 2}>
                      <Controller name="baths" control={control}
                                  render={({field}) => (
                                    <div>
                                    <TextField {...field} id="edit-baths" select
                                               fullWidth label="Baths"
                                               variant="outlined"
                                               key={`edit-baths`}
                                    >
                                      <MenuItem key={`baths-all`}
                                                value={0}><em>-Any-</em></MenuItem>
                                      {
                                        [1, 1.5, 2, 2.5, 3, 3.5, 4, 4.5, 5, 5.5].map((v, i) => {
                                          return <MenuItem key={`baths-${i}`}
                                                           value={v}>{v}</MenuItem>
                                        })
                                      }
                                    </TextField>
                                    </div>
                                  )
                                  }
                      />
                    </Box>
                  </Grid>

                  {!isMobile &&
                  <Grid item xs={12} md={2}>
                    <Button key={`edit-submit`} name="search_submit"
                            disableElevation variant='contained'
                            style={{width: '100%', height: '56px'}}
                            color='primary' type="submit">SEARCH
                    </Button>
                  </Grid>
                  }
                  {isMobile &&
                  <>
                    <Grid item xs={6}>
                      <Box align="right">
                        <Button disableElevation variant='contained'
                                style={{
                                  width: '100%',
                                  height: '56px',
                                  fontSize: '1rem'
                                }} onClick={() => handleSearchFormReset()}
                                className={`reset-btn`}>RESET
                          FILTERS</Button>
                      </Box>
                    </Grid>
                    <Grid item xs={6}>
                      <Button disableElevation variant='contained'
                              style={{width: '100%', height: '56px'}}
                              color='primary' type="submit" aria-label="Search our listings">SEARCH</Button>
                    </Grid>
                  </>
                  }

                    </Grid>

                    {!isMobile &&
                      <Grid item xs={12}>
                        <Box align="right">
                          <Button variant="text"
                            onClick={() => handleSearchFormReset()}
                            className={`reset-btn`}>RESET FILTERS</Button>
                        </Box>
                      </Grid>
                    }
                  </form>

                </ConditionalWrapper>
              </Box>
            </div>
          }

          <ListingsGrid {...searchQ} key={`search-comp-${key}`} pageCurr={resetPager} noresult={<SearchNoResults />} />

          {ctaLinks?.length > 0 &&
            <Box display={`flex`} py={3}>
              <Grid container justifyContent={`center`} spacing={4}>
                {ctaLinks?.map((btn, index) => (
                  <Grid item key={`btn-i-${index}`}>
                    <Button disableElevation variant="contained" color="primary"
                      href={btn.second}>{parse(btn.first)}</Button>
                  </Grid>
                ))}
              </Grid>
            </Box>
          }
        </>
      }
    </Box>
  );
};

export default SalesforceSearchForm;
